/**
 * Multi-country wizard for editing scheduled sent prepared drops (edit-batch API).
 */

import { useState } from 'react';
import type { FC } from 'react';

import { ModalPortalSurfaceContext } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { CountriesStep, CreativeStep } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/steps';
import type { EditSentDropPayload, SentEditSaveMeta } from '@/features/smswiser-campaigns/components/modals/sent/EditSentDropModal.types';

import { useMultiCountrySentScheduledEditModalState } from './hooks/useMultiCountrySentScheduledEditModalState';
import { SentScheduledWizardFooter, SentScheduledWizardHeader } from './shell/SentScheduledWizardChrome';
import { SentScheduledConfigStep } from './steps';
import { backdropClick } from '@/components/ui';

interface CatalogForCountry {
  gateways: Array<{ id: number; name: string; pricePerSms?: number }>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: Array<{ value: string; displayName: string }>;
  leadsLists?: Array<{ id: number; name: string }>;
}

interface Props {
  selectedIds: string[];
  mergedDrops: PreparedDrop[];
  catalogByCountry: Record<string, CatalogForCountry>;
  senderNames: string[];
  smsTexts: string[];
  onClose: () => void;
  onSave: (payloads: EditSentDropPayload[], meta?: SentEditSaveMeta) => void | Promise<void>;
}

export const MultiCountrySentScheduledEditModal: FC<Props> = (props) => {
  const { selectedIds, mergedDrops, catalogByCountry, senderNames, smsTexts, onClose, onSave } = props;
  const [portalSurface, setPortalSurface] = useState<HTMLDivElement | null>(null);
  const state = useMultiCountrySentScheduledEditModalState({
    selectedIds,
    mergedDrops,
    catalogByCountry,
    onClose,
    onSave,
  });

  return (
    <ModalPortalSurfaceContext.Provider value={portalSurface}>
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm"
        onClick={backdropClick(onClose)}
      >
        <div
          ref={setPortalSurface}
          className="relative flex max-h-[94vh] w-[96vw] max-w-[900px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <SentScheduledWizardHeader
            form={state.form}
            affectedCount={state.affectedScheduledCount}
            onClose={onClose}
            isBusy={state.isBusy}
          />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {state.form.step === 1 && <SentScheduledConfigStep form={state.form} isBusy={state.isBusy} />}
            {state.form.step === 2 && (
              <CountriesStep
                form={state.form}
                selectedIds={selectedIds}
                mergedDrops={mergedDrops}
                catalogByCountry={catalogByCountry}
                sourceCountryKey={state.sourceCountryKey}
                affectedDraftCount={state.affectedScheduledCount}
                isBusy={state.isBusy}
                propagationMatchKind="scheduled_sent"
                affectedBannerNoun="scheduled drops"
                staticTagMatchCountUnit={{ singular: 'scheduled drop', plural: 'scheduled drops' }}
                staticTagPropagationDescription="First row: your selected country (always included). Other rows add matching scheduled drops by shared tag."
              />
            )}
            {state.form.step === 3 && (
              <CreativeStep
                form={state.creativeForm}
                creative={state.creative}
                countryKeys={state.form.allCountryKeys}
                dropLabels={state.allDropLabels}
                dropLabelsByCountry={state.dropLabelsByCountry}
                sendersList={senderNames}
                textsList={smsTexts}
                isBusy={state.isBusy}
              />
            )}
          </div>
          <SentScheduledWizardFooter
            form={state.form}
            onSave={state.handleSave}
            onClose={onClose}
            isBusy={state.isBusy}
          />
        </div>
      </div>
    </ModalPortalSurfaceContext.Provider>
  );
};
